import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";
import { ShareIcon, TrashIcon } from "lucide-react-native";
import React, { memo } from "react";

import AppleStyleSwipeableRow from "@/components/molecules/AppleStyleSwipeableRow";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { NotesNoteFragment } from "@/generated/graphql";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

/**
 * Returns the author display name for a note
 * @param note - The note fragment containing user data
 */
const getAuthorName = (note: NotesNoteFragment): string => {
  const attributes =
    note.user?.cognitoUser?.Attributes ??
    note.user?.cognitoUser?.UserAttributes ??
    null;
  return getCognitoDisplayName(attributes, "Unknown");
};

/**
 * Returns the themed background class for note item containers
 * @param isDark - Whether dark mode is active
 */
const getItemBgClass = (isDark: boolean): string =>
  isDark ? "bg-surface-subtle" : "bg-surface-muted";

/**
 * Returns the themed background class for action buttons
 * @param isDark - Whether dark mode is active
 */
const getButtonBgClass = (isDark: boolean): string =>
  isDark ? "bg-background-gray" : "bg-surface-muted";

/**
 * Returns the themed text class for note content
 * @param isDark - Whether dark mode is active
 */
const getTextClass = (isDark: boolean): string =>
  isDark ? "text-white" : "text-content-primary";

/**
 * Props for NotesItemView component
 */
interface Props {
  note: NotesNoteFragment;
  loading?: boolean;
  isSmallScreen: boolean;
  /** Handler for delete action - undefined when user is not the owner */
  onDelete?: () => void;
  /** Handler for share action - undefined when user is not the owner */
  onShare?: () => void;
  /** Whether the current user owns this note */
  isOwner: boolean;
  isDark: boolean;
}

/**
 * Pure view component for note item with ownership-aware actions.
 * Shows author attribution for non-owned notes and "Shared" badge.
 * Only shows delete/share actions when the current user is the owner.
 * @param root0 - Component props
 * @param root0.note - Note data to display
 * @param root0.loading - Whether delete operation is in progress
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.onDelete - Handler for delete button press, undefined when not owner
 * @param root0.onShare - Handler for share button press, undefined when not owner
 * @param root0.isOwner - Whether the current user is the note author
 * @param root0.isDark - Whether dark theme is active
 */
const NotesItemView = ({
  note,
  loading,
  isSmallScreen,
  onDelete,
  onShare,
  isOwner,
  isDark,
}: Props) =>
  isSmallScreen ? (
    <AppleStyleSwipeableRow
      onDelete={loading || !onDelete ? undefined : onDelete}
    >
      <Stack className={`gap-2 rounded-sm px-5 py-3 ${getItemBgClass(isDark)}`}>
        <Text variant="unstyled" className={getTextClass(isDark)}>
          {note.body}
        </Text>
        {!isOwner && (
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="caption" className="text-content-muted">
              {getAuthorName(note)}
            </Text>
            <Box className="rounded-none bg-status-warning-surface px-1 py-micro">
              <Text
                variant="micro-strong"
                className="uppercase text-status-warning"
              >
                Shared
              </Text>
            </Box>
          </Stack>
        )}
      </Stack>
    </AppleStyleSwipeableRow>
  ) : (
    <Stack
      direction="horizontal"
      className={`items-center justify-between gap-2 rounded-sm px-3 py-2 ${getItemBgClass(isDark)}`}
    >
      <Stack className="flex-1 gap-1">
        <Text variant="body" className={getTextClass(isDark)}>
          {note.body}
        </Text>
        {!isOwner && (
          <Stack direction="horizontal" className="items-center gap-2">
            <Text variant="caption" className="text-content-muted">
              {getAuthorName(note)}
            </Text>
            <Box className="rounded-none bg-status-warning-surface px-1 py-micro">
              <Text
                variant="micro-strong"
                className="uppercase text-status-warning"
              >
                Shared
              </Text>
            </Box>
          </Stack>
        )}
      </Stack>
      <Stack direction="horizontal" className="items-center gap-1">
        {!!onShare && (
          <Button
            size="lg"
            variant="link"
            shape="pill"
            className={`px-3 ${getButtonBgClass(isDark)}`}
            onPress={onShare}
            accessible
            accessibilityLabel="Share note"
            accessibilityRole="button"
            icon={ShareIcon}
            iconTone="info"
          />
        )}
        {!!onDelete && (
          <Button
            testID={TEST_ID_PLAYER_DETAIL.REMOVE_NOTE_BUTTON}
            size="lg"
            variant="link"
            disabled={loading}
            shape="pill"
            className={`px-3 ${getButtonBgClass(isDark)}`}
            onPress={onDelete}
            accessible
            accessibilityLabel="Delete note"
            accessibilityRole="button"
            icon={TrashIcon}
            iconTone="info"
          />
        )}
      </Stack>
    </Stack>
  );

NotesItemView.displayName = "NotesItemView";

export default memo(NotesItemView);
